import React, { useState, useRef } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { useQuery, useMutation } from '@apollo/client';
import { Formik, FormikProps } from 'formik';

import { FETCH_UNIFIED_CONTACTS } from '@/services/unifiedContacts';
import Page from '@/components/layout/page';
import { withAuth } from '@/components/with_auth';
import routes from '@/services/routes';
import {
    handleValidationErrors,
    onCompletedHandler,
    onErrorHandler,
} from '@/services/apollo';
import styles from '@/components/index/Index.module.css';
import RightPanelGroup from '@/components/commons/right_panel/RightPanelGroup';
import ProfileCardSolid from '@/components/commons/icons/ProfileCardSolid';
// import ShareAll from '@/components/commons/icons/ShareAll';
// import SearchGlass from '@/components/commons/icons/SearchGlass';
// import UserAddSolid from '@/components/commons/icons/UserAddSolid';
import FolderSolid from '@/components/commons/icons/FolderSolid';
import BellSolid from '@/components/commons/icons/BellSolid';
import OrderedList from '@/components/commons/icons/OrderedList';
import CloseIcon from '@/components/commons/icons/CloseIcon';
import BellActive from '@/components/commons/icons/BellActive';
import {
    initializedAtom,
    onboardingComplete,
    onboardingRemindLater,
    userAtom,
    defaultBlankUserAtom,
} from '@/components/with_auth/State';
import { haveErrors, reportGeneralError } from '@/components/onboarding/Shared';
import { UPDATE_USER } from '@/components/onboarding/Actions';

import MySixtyDashboard, { type UnifiedContactStats } from '@/components/my-sixty';
import { FETCH_UNIFIED_CONTACT_STATS } from '@/components/my-sixty/Actions';

function MySixty() {
    const user = useRecoilValue(defaultBlankUserAtom);
    const remindLater = useRecoilValue(onboardingRemindLater);
    const isOnboardingComplete = useRecoilValue(onboardingComplete);
    const [updateUser] = useMutation(UPDATE_USER);
    const remindLaterformRef = useRef<FormikProps<any>>(null);
    const [sidePanelOpen, setSidePanelOpen] = useState(true);
    const [isError, setIsError] = useState(false);
    const [contactStats, setContactStats] = useState<UnifiedContactStats>({});
    const [contacts, setContacts] = useState({});

    const notOnboardedYet = !isOnboardingComplete && !remindLater;

    const setters = {
        setUser: useSetRecoilState(userAtom),
        setInitialized: useSetRecoilState(initializedAtom),
    };

    useQuery(FETCH_UNIFIED_CONTACTS, {
        variables: {
            visible: true,
            human: true,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (info) => {
            const contactsData = info
                ?.user?.unifiedContacts?.filter((contact) => contact.mappings.length > 0);
            const contactsByCloseness = {};
            contactsData.forEach((contact) => {
                const circle = contact.effectiveCircle;
                if (!contactsByCloseness[circle]) {
                    contactsByCloseness[circle] = [];
                }
                contactsByCloseness[circle].push(contact);
            });

            // Sort contactsByCloseness lists by whether or not there's a photo:
            Object.keys(contactsByCloseness).forEach((key) => {
                contactsByCloseness[key].sort((a, b) => {
                    if (a.photoImageUrl && !b.photoImageUrl) {
                        return -1;
                    }
                    if (!a.photoImageUrl && b.photoImageUrl) {
                        return 1;
                    }
                    return 0;
                });
            });

            setContacts(contactsByCloseness);
        }),
    });

    const handleReminder = () => {
        if (remindLaterformRef.current) {
            remindLaterformRef.current.handleSubmit();
        }
    };

    const handleUpdateUser = (attributes, setSubmitting, setStatus) => {
        updateUser({
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(
                () => { },
                (data) => {
                    setSubmitting(false);
                    const { errors } = data.updateUser;
                    if (haveErrors(errors)) {
                        handleValidationErrors(errors, setStatus, 'user');
                        return;
                    }
                    setStatus({ errors: {} });
                    setters.setUser(data.updateUser.user);
                    setters.setInitialized(true);
                },
            ),
        });
    };

    const setContactStatsHandler = (data) => {
        const result: UnifiedContactStats = {};
        data.unifiedContactsByCloseness.forEach((closeness) => {
            result[closeness.effectiveCircle] = closeness.count;
        });
        result.newContacts = data.newUnifiedContacts;
        setContactStats(result);
    };

    const {
        loading,
    } = useQuery(FETCH_UNIFIED_CONTACT_STATS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, setContactStatsHandler),
    });

    const lateralGroupsRaw = [
        {
            key: 'master-your-network',
            title: 'Master your network',
            titleStyle: '',
            items: [
                {
                    key: 'claim-your-profile',
                    onboardingKey: 'hasCreatedBio',
                    name: 'Claim your profile',
                    route: routes.BIO,
                    icon: <ProfileCardSolid className="w-full h-full text-cleanSlate" />,
                    buttonStyles: '',
                    textStyles: '',
                },
                // {
                //     key: 'share-your-profile',
                //     onboardingKey: 'hasCreatedBio',
                //     name: 'Share your profile',
                //     route: routes.MY_SIXTY,
                //     icon: <ShareAll className="w-full h-full text-cleanSlate" />,
                //     buttonStyles: '',
                //     textStyles: '',
                // },
                // {
                //     key: 'search-for-new-contacts',
                //     onboardingKey: 'hasCreatedBio',
                //     name: 'Search for new contacts',
                //     route: routes.MY_SIXTY,
                //     icon: <SearchGlass className="w-full h-full text-cleanSlate" />,
                //     buttonStyles: '',
                //     textStyles: '',
                // },
                // {
                //     key: 'get-or-make-an-introduction',
                //     onboardingKey: 'hasCreatedBio',
                //     name: 'Get or make an introduction',
                //     route: routes.MY_SIXTY,
                //     icon: <UserAddSolid className="w-full h-full text-cleanSlate" />,
                //     buttonStyles: '',
                //     textStyles: '',
                // },
            ],
        },
        {
            key: 'take-back-your-inbox',
            title: 'Take back your inbox',
            titleStyle: '',
            items: [
                {
                    key: 'set-up-smart-folders',
                    onboardingKey: 'hasCreatedFolders',
                    name: 'Set up Smart Folders',
                    route: routes.MONITORED_LABELS,
                    icon: <FolderSolid className="w-full h-full text-cleanSlate" />,
                    buttonStyles: '',
                    textStyles: '',
                },
            ],
        },
        {
            key: 'have-more-effective-meetings',
            title: 'Have more effective meetings',
            titleStyle: '',
            items: [
                {
                    key: 'create-a-smart-alert',
                    onboardingKey: 'hasCreatedAlerts',
                    name: 'Create a Smart Alert',
                    route: routes.ALERTS,
                    icon: <BellSolid className="w-full h-full text-cleanSlate" />,
                    buttonStyles: '',
                    textStyles: '',
                },
                {
                    key: 'create-a-custom-agenda',
                    onboardingKey: 'hasCreatedAgendas',
                    name: 'Create a custom agenda',
                    route: routes.AGENDAS,
                    icon: <OrderedList className="w-full h-full text-cleanSlate" />,
                    buttonStyles: '',
                    textStyles: '',
                },
            ],
        },
    ];
    const lateralGroups = lateralGroupsRaw.map((group) => {
        const items = group.items.filter((item) => {
            if (user[item.onboardingKey]) {
                return false;
            }
            return true;
        });
        if (items.length > 0) {
            return {
                ...group,
                items,
            };
        }
        return null;
    }).filter((group) => group !== null);

    return (
        <Page>
            <div className={`flex flex-wrap pl-4 sm:pl-8 pr-0 sm:pr-6 md:pr-0 ${styles.home} relative`}>
                <div className="flex-2 w-full pt-8 pb-8 pr-4 md:pr-10">
                    <div className={`${styles.homeContent}bg-lightGray`}>
                        <div className={styles.homeStats}>
                            <MySixtyDashboard
                                loading={loading}
                                error={isError}
                                statistics={contactStats}
                                contacts={contacts}
                                setSidePanelOpen={setSidePanelOpen}
                            />
                        </div>
                    </div>
                </div>
                {(notOnboardedYet && sidePanelOpen) && (
                    <div className="w-full h-full py-0 mr-0 mt-0 pr-2 md:py-0 md:w-80 bg-cleanSlate bg-opacity-90 absolute right-0">
                        <button aria-label="Close" type="button" className="flex w-full items-center justify-end mt-3" onClick={() => setSidePanelOpen(false)}><CloseIcon className="w-3 text-shadeStoneGray" /></button>
                        <div className="w-full md:min-w-48 p-4 pt-10">
                            <h3 className="text-cirrusWhite text-[20px] font-semibold">Let&apos;s get you rolling!</h3>
                            <p className="pb-6 pt-2 text-cirrusWhite">Sixty includes all sorts of amazing features designed to maximize your daily work and supercharge your network. </p>
                            <RightPanelGroup data={lateralGroups} />
                            <div className="flex items-center justify-center mt-14">
                                <BellActive className="w-[12px] text-cirrusWhite" />
                                <button
                                    type="button"
                                    className="pl-1 text-[14px] text-cirrusWhite"
                                    onClick={handleReminder}
                                >
                                    Remind me later
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Formik
                innerRef={remindLaterformRef}
                onSubmit={(
                    _frm,
                    { setStatus, setSubmitting },
                ) => {
                    const setupReminderAt = new Date();
                    setupReminderAt.setUTCDate(
                        setupReminderAt.getUTCDate() + 7,
                    );

                    const attributes = {
                        setupReminderAt,
                    };

                    handleUpdateUser(
                        attributes,
                        setSubmitting,
                        setStatus,
                    );
                }}
                initialValues={{
                    setupReminderAt: user.setupReminderAt,
                }}
            />
        </Page>
    );
}
export default withAuth(MySixty);
